import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import {
  BarChart3, TrendingUp, Calendar, Download, Printer, Filter,
  Package, FlaskConical, Users, DollarSign, Target, ArrowUp, ArrowDown,
  FileSpreadsheet
} from 'lucide-react';
import {
  BarChart, Bar, LineChart, Line, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, ComposedChart
} from 'recharts';
import { reportsAPI, branchesAPI, bookingsAPI } from '../../services/api';
import toast from 'react-hot-toast';
import GregorianDatePicker from '../../components/GregorianDatePicker';

const SalesAnalysis = () => {
  const [loading, setLoading] = useState(true);
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [selectedBranch, setSelectedBranch] = useState('');
  const [branches, setBranches] = useState([]);
  const [analysisData, setAnalysisData] = useState({
    totalRevenue: 0,
    totalTests: 0,
    totalPackages: 0,
    topTests: [],
    topEmployees: [],
    topPackages: [],
    testVsPackageRevenue: [],
    dailyTrend: [],
    customerInsights: {
      totalCustomers: 0,
      avgTestsPerCustomer: 0,
      avgRevenuePerCustomer: 0
    },
    performanceMetrics: {
      growthRate: 0,
      conversionRate: 0,
      repeatCustomerRate: 0
    }
  });

  useEffect(() => {
    // Set default date range (last 30 days)
    const now = new Date();
    const thirtyDaysAgo = new Date();
    thirtyDaysAgo.setDate(now.getDate() - 30);

    setDateFrom(thirtyDaysAgo.toISOString().split('T')[0]);
    setDateTo(now.toISOString().split('T')[0]);

    // Fetch branches
    fetchBranches();
  }, []);

  const fetchBranches = async () => {
    try {
      const response = await branchesAPI.getAll({ limit: 100 });
      // paginatedResponse returns: { data: { items, total, page, limit } }
      const data = response.data?.data?.items || response.data?.items || [];
      setBranches(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Error fetching branches:', error);
    }
  };

  useEffect(() => {
    if (dateFrom && dateTo) {
      fetchAnalysisData();
    }
  }, [dateFrom, dateTo, selectedBranch]);

  const fetchAnalysisData = async () => {
    setLoading(true);
    try {
      const params = {
        date_from: dateFrom,
        date_to: dateTo
      };
      if (selectedBranch) params.branch_id = selectedBranch;

      const response = await reportsAPI.getSalesAnalysis(params);

      const data = response.data?.data;

      if (data) {
        // Backend returns: summary, topTests, topPackages, testVsPackageRevenue, dailyTrend, customerInsights, performanceMetrics
        const summary = data.summary || {};
        
        setAnalysisData({
          totalRevenue: summary.totalRevenue || 0,
          totalTests: summary.totalTests || 0,
          totalPackages: summary.totalPackages || 0,
          topTests: data.topTests || [],
          topPackages: data.topPackages || [],
          testVsPackageRevenue: data.testVsPackageRevenue || [],
          dailyTrend: data.dailyTrend || [],
          topEmployees: data.topEmployees || [],
          customerInsights: data.customerInsights || {
            totalCustomers: summary.totalCustomers || 0,
            avgTestsPerCustomer: 0,
            avgRevenuePerCustomer: 0
          },
          performanceMetrics: data.performanceMetrics || {
            growthRate: 0
          }
        });
      }

    } catch (error) {
      console.error('Error fetching analysis data:', error);
      toast.error('حدث خطأ أثناء جلب بيانات التحليل');
    }
    setLoading(false);
  };

  const handlePrint = () => {
    window.print();
  };

  const [exporting, setExporting] = useState(false);

  const handleExportToExcel = async () => {
    setExporting(true);
    const loadingToast = toast.loading('جاري تجهيز ملف الإكسل...');
    
    try {
      // Fetch all bookings within the date range with full details
      const params = {
        date_from: dateFrom,
        date_to: dateTo
      };
      if (selectedBranch) params.branch_id = selectedBranch;

      const response = await bookingsAPI.export(params);
      const bookings = response.data?.data || [];

      if (bookings.length === 0) {
        toast.dismiss(loadingToast);
        toast.error('لا توجد حجوزات في الفترة المحددة');
        setExporting(false);
        return;
      }

      // Status translations
      const statusMap = {
        'new': 'جديد',
        'pending': 'معلق',
        'sample_collected': 'تم التجميع',
        'collected': 'تم التجميع',
        'in_progress': 'قيد المعالجة',
        'result_ready': 'النتيجة جاهزة',
        'completed': 'مكتمل',
        'approved': 'معتمد',
        'released': 'تم التسليم',
        'cancelled': 'ملغي'
      };

      const paymentStatusMap = {
        'unpaid': 'غير مدفوع',
        'partial': 'مدفوع جزئياً',
        'paid': 'مدفوع بالكامل'
      };

      const paymentMethodMap = {
        'cash': 'نقدي',
        'card': 'بطاقة',
        'cash_card': 'نقدي + بطاقة',
        'bank_transfer': 'تحويل بنكي',
        'insurance': 'تأمين',
        'tamara': 'تمارا',
        'tabby': 'تابي'
      };

      const visitTypeMap = {
        'in_lab': 'في المختبر',
        'home_visit': 'زيارة منزلية',
        'contractor': 'مختبر متعاقد'
      };

      // Create Excel data
      const headers = [
        'رقم الحجز',
        'الباركود',
        'تاريخ الحجز',
        'وقت الحجز',
        'اسم العميل',
        'رقم الهوية',
        'رقم الجوال',
        'الجنسية',
        'الفرع',
        'نوع الزيارة',
        'التحاليل',
        'عدد التحاليل',
        'الباقات',
        'عدد الباقات',
        'المبلغ الإجمالي',
        'الخصم',
        'كود الخصم',
        'الضريبة',
        'المبلغ الصافي',
        'المبلغ المدفوع',
        'المبلغ المتبقي',
        'طريقة الدفع',
        'المبلغ النقدي',
        'مبلغ البطاقة',
        'حالة الدفع',
        'حالة الحجز',
        'شركة التأمين',
        'مستعجل',
        'الموظف',
        'ملاحظات العميل',
        'ملاحظات داخلية',
        'تاريخ الإنشاء'
      ];

      const rows = bookings.map(booking => {
        // Get tests names (only items with lab_test_id)
        const testsArr = booking.tests?.filter(t => t.lab_test_id) || [];
        const tests = testsArr.length > 0
          ? testsArr.map(t => t.lab_test_id?.name_ar || t.lab_test_id?.name_en || '-').join(' | ')
          : '-';

        // Get packages names (only items with package_id)
        const packagesArr = booking.tests?.filter(t => t.package_id) || [];
        const packages = packagesArr.length > 0
          ? packagesArr.map(p => p.package_id?.name || '-').join(' | ')
          : '-';

        const bookingDate = new Date(booking.booking_date);
        const createdDate = new Date(booking.created_at);

        return [
          booking.booking_no || '-',
          booking.barcode_no || '-',
          bookingDate.toLocaleDateString('en-GB'),
          bookingDate.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' }),
          booking.customer_id?.full_name || '-',
          booking.customer_id?.national_id || '-',
          booking.customer_id?.phone || '-',
          booking.customer_id?.nationality || '-',
          booking.branch_id?.branch_name || '-',
          visitTypeMap[booking.visit_type] || booking.visit_type || '-',
          tests,
          testsArr.length,
          packages,
          packagesArr.length,
          (booking.total_amount || 0).toFixed(2),
          (booking.discount_amount || 0).toFixed(2),
          booking.coupon_code || '-',
          (booking.vat_amount || 0).toFixed(2),
          (booking.net_amount || 0).toFixed(2),
          (booking.paid_amount || 0).toFixed(2),
          ((booking.net_amount || 0) - (booking.paid_amount || 0)).toFixed(2),
          paymentMethodMap[booking.payment_method] || booking.payment_method || '-',
          booking.cash_amount ? (booking.cash_amount).toFixed(2) : '-',
          booking.card_amount ? (booking.card_amount).toFixed(2) : '-',
          paymentStatusMap[booking.payment_status] || booking.payment_status || '-',
          statusMap[booking.status] || booking.status || '-',
          booking.insurance_company_id?.name || '-',
          booking.is_urgent ? 'نعم' : 'لا',
          booking.created_by_user_id?.name || '-',
          booking.customer_notes || '-',
          booking.internal_notes || '-',
          createdDate.toLocaleDateString('en-GB') + ' ' + createdDate.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })
        ];
      });

      // Calculate totals
      const totals = bookings.reduce((acc, b) => ({
        total: acc.total + (b.total_amount || 0),
        discount: acc.discount + (b.discount_amount || 0),
        vat: acc.vat + (b.vat_amount || 0),
        net: acc.net + (b.net_amount || 0),
        paid: acc.paid + (b.paid_amount || 0)
      }), { total: 0, discount: 0, vat: 0, net: 0, paid: 0 });

      // Add summary rows at the end
      const summaryRows = [
        [],
        ['', '', '', '', '', '', '', '', '', '', '', '', '', 'الإجماليات:', totals.total.toFixed(2), totals.discount.toFixed(2), '', totals.vat.toFixed(2), totals.net.toFixed(2), totals.paid.toFixed(2), (totals.net - totals.paid).toFixed(2)],
        [],
        [],
        ['═══════════════════════════════════════════════════════════════════════════════════'],
        ['تقرير تحليل المبيعات - ملخص'],
        ['═══════════════════════════════════════════════════════════════════════════════════'],
        [`الفترة: من ${dateFrom} إلى ${dateTo}`],
        [`عدد الحجوزات: ${bookings.length}`],
        [`إجمالي المبالغ قبل الخصم: ${totals.total.toFixed(2)} ر.س`],
        [`إجمالي الخصومات: ${totals.discount.toFixed(2)} ر.س`],
        [`إجمالي الضريبة: ${totals.vat.toFixed(2)} ر.س`],
        [`إجمالي الإيرادات (صافي): ${totals.net.toFixed(2)} ر.س`],
        [`إجمالي المدفوع: ${totals.paid.toFixed(2)} ر.س`],
        [`إجمالي المتبقي: ${(totals.net - totals.paid).toFixed(2)} ر.س`],
        ['═══════════════════════════════════════════════════════════════════════════════════']
      ];

      // Combine all data
      const csvData = [headers, ...rows, ...summaryRows];

      // Convert to CSV with proper escaping
      const escapeCsvValue = (val) => {
        if (val === null || val === undefined) return '';
        const str = String(val);
        if (str.includes(',') || str.includes('"') || str.includes('\n')) {
          return `"${str.replace(/"/g, '""')}"`;
        }
        return str;
      };

      const csv = csvData.map(row => row.map(escapeCsvValue).join(',')).join('\n');
      
      // Create and download file
    const blob = new Blob(['\ufeff' + csv], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
      link.download = `تقرير_الحجوزات_${dateFrom}_${dateTo}.csv`;
    link.click();

      toast.dismiss(loadingToast);
      toast.success(`تم تصدير ${bookings.length} حجز بنجاح`);
    } catch (error) {
      console.error('Export error:', error);
      toast.dismiss(loadingToast);
      toast.error('حدث خطأ أثناء تصدير البيانات');
    }
    setExporting(false);
  };

  if (loading) {
    return (
      <div className="container mx-auto px-6 py-8 flex items-center justify-center h-96">
        <div className="w-12 h-12 border-4 border-blue-200 border-t-blue-600 rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F8FAFC]" dir="rtl">
      <div className="max-w-[1600px] mx-auto px-4 sm:px-6 lg:px-8 py-6 lg:py-8">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6"
        >
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-2xl bg-[#1E293B] flex items-center justify-center">
              <BarChart3 className="w-6 h-6 text-slate-100" strokeWidth={2} />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-[#1E293B] tracking-tight">تحليل المبيعات</h1>
              <p className="text-slate-500 text-sm mt-1">
                من {new Date(dateFrom).toLocaleDateString('en-GB')} إلى {new Date(dateTo).toLocaleDateString('en-GB')}
              </p>
            </div>
          </div>
          <div className="flex gap-2 print:hidden">
            <motion.button
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              onClick={handlePrint}
              className="inline-flex items-center justify-center gap-2 px-5 py-2.5 bg-blue-600 text-white rounded-xl font-medium text-sm hover:bg-blue-700 transition-all duration-200 shadow-sm"
            >
              <Printer className="w-4 h-4" strokeWidth={2.5} />
              <span>طباعة</span>
            </motion.button>
            <motion.button
              whileHover={{ scale: exporting ? 1 : 1.02 }}
              whileTap={{ scale: exporting ? 1 : 0.98 }}
              onClick={handleExportToExcel}
              disabled={exporting}
              className={`inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-xl font-medium text-sm transition-all duration-200 ${
                exporting 
                  ? 'bg-gray-100 text-gray-400 cursor-not-allowed' 
                  : 'bg-green-600 text-white hover:bg-green-700 shadow-sm'
              }`}
            >
              {exporting ? (
                <>
                  <div className="w-4 h-4 border-2 border-gray-300 border-t-gray-600 rounded-full animate-spin" />
                  <span>جاري التصدير...</span>
                </>
              ) : (
                <>
                  <FileSpreadsheet className="w-4 h-4" strokeWidth={2.5} />
                  <span>تصدير إكسل</span>
                </>
              )}
            </motion.button>
          </div>
        </motion.div>

        {/* Filters */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 mb-6 print:hidden"
        >
          <div className="flex flex-col md:flex-row items-center gap-4">
            <div className="flex items-center gap-2 text-gray-400 min-w-fit">
              <Filter className="w-5 h-5" />
              <span className="text-sm font-medium text-gray-600">تصفية حسب:</span>
            </div>

            <div className="flex-1 grid grid-cols-1 md:grid-cols-3 gap-4 w-full">
              {/* Branch Filter */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">الفرع</label>
                <div className="relative">
                  <select
                    value={selectedBranch}
                    onChange={(e) => setSelectedBranch(e.target.value)}
                    className="w-full px-4 py-2 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 appearance-none bg-white"
                  >
                    <option value="">جميع الفروع</option>
                    {branches.map(branch => (
                      <option key={branch._id} value={branch._id}>{branch.branch_name}</option>
                    ))}
                  </select>
                </div>
              </div>

              <GregorianDatePicker
                label="من تاريخ"
                  value={dateFrom}
                onChange={setDateFrom}
                maxDate={dateTo || undefined}
                />
              <GregorianDatePicker
                label="إلى تاريخ"
                  value={dateTo}
                onChange={setDateTo}
                minDate={dateFrom || undefined}
                />
            </div>
          </div>
        </motion.div>

        {/* KPI Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6"
          >
            <div className="flex items-center justify-between mb-4">
              <div className="p-3 bg-blue-100 rounded-xl">
                <DollarSign className="w-6 h-6 text-blue-600" />
              </div>
              <div className={`flex items-center gap-1 text-xs ${analysisData.performanceMetrics.growthRate >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                {analysisData.performanceMetrics.growthRate >= 0 ? <ArrowUp className="w-3 h-3" /> : <ArrowDown className="w-3 h-3" />}
                {Math.abs(analysisData.performanceMetrics.growthRate).toFixed(1)}%
              </div>
            </div>
            <p className="text-sm text-gray-600 mb-1">إجمالي الإيرادات</p>
            <p className="text-2xl font-bold text-gray-900">{analysisData.totalRevenue.toFixed(2)} ر.س</p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.25 }}
            className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6"
          >
            <div className="flex items-center justify-between mb-4">
              <div className="p-3 bg-blue-100 rounded-xl">
                <FlaskConical className="w-6 h-6 text-blue-600" />
              </div>
            </div>
            <p className="text-sm text-gray-600 mb-1">إجمالي التحاليل</p>
            <p className="text-2xl font-bold text-gray-900">{analysisData.totalTests}</p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6"
          >
            <div className="flex items-center justify-between mb-4">
              <div className="p-3 bg-blue-100 rounded-xl">
                <Package className="w-6 h-6 text-blue-600" />
              </div>
            </div>
            <p className="text-sm text-gray-600 mb-1">إجمالي الباقات</p>
            <p className="text-2xl font-bold text-gray-900">{analysisData.totalPackages}</p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.35 }}
            className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6"
          >
            <div className="flex items-center justify-between mb-4">
              <div className="p-3 bg-blue-100 rounded-xl">
                <Users className="w-6 h-6 text-blue-600" />
              </div>
            </div>
            <p className="text-sm text-gray-600 mb-1">إجمالي العملاء</p>
            <p className="text-2xl font-bold text-gray-900">{analysisData.customerInsights.totalCustomers}</p>
          </motion.div>
        </div>

        {/* Main Charts */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
          {/* Daily Trend */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.4 }}
            className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6"
          >
            <h3 className="text-lg font-bold text-gray-800 mb-6">اتجاه المبيعات اليومية</h3>
            {analysisData.dailyTrend.length > 0 ? (
              <ResponsiveContainer width="100%" height={300}>
                <ComposedChart data={analysisData.dailyTrend}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" />
                  <XAxis
                    dataKey="date"
                    stroke="#9ca3af"
                    fontSize={10}
                    tickFormatter={(value) => new Date(value).getDate()}
                  />
                  <YAxis yAxisId="left" stroke="#9ca3af" fontSize={12} />
                  <YAxis yAxisId="right" orientation="right" stroke="#9ca3af" fontSize={12} />
                  <Tooltip
                    contentStyle={{
                      background: '#fff',
                      border: '1px solid #e5e7eb',
                      borderRadius: '8px',
                      fontSize: '12px'
                    }}
                    labelFormatter={(value) => new Date(value).toLocaleDateString('ar-SA-u-ca-gregory')}
                    formatter={(value, name) => {
                      if (name === 'revenue') return [`${value.toFixed(2)} ر.س`, 'الإيرادات'];
                      if (name === 'tests') return [value, 'التحاليل'];
                      if (name === 'packages') return [value, 'الباقات'];
                      return value;
                    }}
                  />
                  <Bar yAxisId="left" dataKey="tests" fill="#3b82f6" radius={[8, 8, 0, 0]} />
                  <Bar yAxisId="left" dataKey="packages" fill="#10b981" radius={[8, 8, 0, 0]} />
                  <Line
                    yAxisId="right"
                    type="monotone"
                    dataKey="revenue"
                    stroke="#8b5cf6"
                    strokeWidth={2}
                    dot={{ fill: '#8b5cf6', r: 3 }}
                  />
                </ComposedChart>
              </ResponsiveContainer>
            ) : (
              <div className="flex items-center justify-center h-64 text-gray-400">
                <p>لا توجد بيانات كافية</p>
              </div>
            )}
          </motion.div>

          {/* Test vs Package Revenue */}
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.45 }}
            className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6"
          >
            <h3 className="text-lg font-bold text-gray-800 mb-6">توزيع الإيرادات</h3>
            {analysisData.testVsPackageRevenue.length > 0 ? (
              <>
                <ResponsiveContainer width="100%" height={250}>
                  <PieChart>
                    <Pie
                      data={analysisData.testVsPackageRevenue}
                      cx="50%"
                      cy="50%"
                      innerRadius={60}
                      outerRadius={90}
                      paddingAngle={analysisData.testVsPackageRevenue.length > 1 ? 5 : 0}
                      dataKey="value"
                      labelLine={false}
                      label={({ cx, cy, midAngle, innerRadius, outerRadius, percent }) => {
                        const RADIAN = Math.PI / 180;
                        const radius = innerRadius + (outerRadius - innerRadius) * 0.5;
                        const x = cx + radius * Math.cos(-midAngle * RADIAN);
                        const y = cy + radius * Math.sin(-midAngle * RADIAN);
                        return percent > 0.05 ? (
                          <text x={x} y={y} fill="white" textAnchor="middle" dominantBaseline="central" fontSize={14} fontWeight="bold">
                            {`${(percent * 100).toFixed(0)}%`}
                          </text>
                        ) : null;
                      }}
                    >
                      {analysisData.testVsPackageRevenue.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Pie>
                    <Tooltip
                      contentStyle={{
                        background: '#fff',
                        border: '1px solid #e5e7eb',
                        borderRadius: '8px',
                        fontSize: '12px'
                      }}
                      formatter={(value) => `${value.toFixed(2)} ر.س`}
                    />
                  </PieChart>
                </ResponsiveContainer>
                <div className={`grid gap-4 mt-4 ${analysisData.testVsPackageRevenue.length === 1 ? 'grid-cols-1' : 'grid-cols-2'}`}>
                  {analysisData.testVsPackageRevenue.map((item, index) => (
                    <div key={index} className="text-center p-3 bg-gray-50 rounded-xl">
                      <div className="flex items-center justify-center gap-2 mb-1">
                        <div className="w-3 h-3 rounded" style={{ background: item.color }} />
                        <span className="text-sm text-gray-600">{item.name}</span>
                      </div>
                      <p className="text-lg font-bold text-gray-900">{item.value.toFixed(2)} ر.س</p>
                    </div>
                  ))}
                </div>
              </>
            ) : (
              <div className="flex items-center justify-center h-64 text-gray-400">
                <p>لا توجد بيانات كافية</p>
              </div>
            )}
          </motion.div>
        </div>

      </div>

      {/* Top Lists */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
        {/* Top Tests */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5 }}
          className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6"
        >
          <h3 className="text-lg font-bold text-gray-800 mb-6 flex items-center gap-2">
            <Target className="w-5 h-5 text-blue-600" />
            أعلى التحاليل مبيعاً
          </h3>
          {analysisData.topTests.length > 0 ? (
            <div className="space-y-3">
              {analysisData.topTests.map((test, index) => (
                <div key={index} className="flex items-center justify-between p-3 bg-gray-50 rounded-xl hover:bg-gray-100 transition-colors">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-lg bg-blue-100 text-blue-600 flex items-center justify-center font-bold text-sm">
                      {index + 1}
                    </div>
                    <div>
                      <p className="font-medium text-gray-800">{test.name || test.test_name}</p>
                      <p className="text-xs text-gray-500">{test.count} مرة</p>
                    </div>
                  </div>
                  {test.revenue > 0 && (
                    <p className="font-bold text-blue-600">{test.revenue.toFixed(2)} ر.س</p>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <div className="flex items-center justify-center py-12 text-gray-400">
              <p>لا توجد بيانات</p>
            </div>
          )}
        </motion.div>

        {/* Top Employees */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.6 }}
          className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6"
        >
          <h3 className="text-lg font-bold text-gray-800 mb-6 flex items-center gap-2">
            <Users className="w-5 h-5 text-purple-600" />
            أفضل 3 موظفين 
          </h3>
          {analysisData.topEmployees.length > 0 ? (
            <div className="space-y-3">
              {analysisData.topEmployees.map((emp, index) => (
                <div key={index} className="flex items-center justify-between p-3 bg-gray-50 rounded-xl hover:bg-gray-100 transition-colors">
                  <div className="flex items-center gap-3">
                    <div className={`w-8 h-8 rounded-lg flex items-center justify-center font-bold text-sm ${index === 0 ? 'bg-yellow-100 text-yellow-600' : index === 1 ? 'bg-gray-200 text-gray-600' : 'bg-orange-100 text-orange-600'}`}>
                      {index + 1}
                    </div>
                    <div>
                      <p className="font-medium text-gray-800">{emp.employee_name}</p>
                      <p className="text-xs text-gray-500">{emp.count} حجوزات</p>
                    </div>
                  </div>
                  <p className="font-bold text-purple-600">{emp.total_sales.toFixed(2)} ر.س</p>
                </div>
              ))}
            </div>
          ) : (
            <div className="flex items-center justify-center py-12 text-gray-400">
              <p>لا توجد بيانات</p>
            </div>
          )}
        </motion.div>

        {/* Top Packages */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.55 }}
          className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6"
        >
          <h3 className="text-lg font-bold text-gray-800 mb-6 flex items-center gap-2">
            <Target className="w-5 h-5 text-green-600" />
            أعلى الباقات مبيعاً 
          </h3>
          {analysisData.topPackages.length > 0 ? (
            <div className="space-y-3">
              {analysisData.topPackages.map((pkg, index) => (
                <div key={index} className="flex items-center justify-between p-3 bg-gray-50 rounded-xl hover:bg-gray-100 transition-colors">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-lg bg-green-100 text-green-600 flex items-center justify-center font-bold text-sm">
                      {index + 1}
                    </div>
                    <div>
                      <p className="font-medium text-gray-800">{pkg.name}</p>
                      <p className="text-xs text-gray-500">{pkg.count} مرة</p>
                    </div>
                  </div>
                  <p className="font-bold text-green-600">{pkg.revenue.toFixed(2)} ر.س</p>
                </div>
              ))}
            </div>
          ) : (
            <div className="flex items-center justify-center py-12 text-gray-400">
              <p>لا توجد بيانات</p>
            </div>
          )}
        </motion.div>
      </div>

      {/* Customer Insights */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.6 }}
        className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6"
      >
        <h3 className="text-lg font-bold text-gray-800 mb-6">رؤى العملاء</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="flex items-center gap-4 p-4 bg-gradient-to-br from-blue-50 to-blue-100/50 rounded-xl">
            <div className="p-3 bg-blue-600 text-white rounded-lg">
              <Users className="w-6 h-6" />
            </div>
            <div>
              <p className="text-sm text-gray-600">متوسط التحاليل لكل عميل</p>
              <p className="text-xl font-bold text-gray-900">{analysisData.customerInsights.avgTestsPerCustomer.toFixed(1)}</p>
            </div>
          </div>
          <div className="flex items-center gap-4 p-4 bg-gradient-to-br from-blue-50 to-blue-100/50 rounded-xl">
            <div className="p-3 bg-blue-600 text-white rounded-lg">
              <DollarSign className="w-6 h-6" />
            </div>
            <div>
              <p className="text-sm text-gray-600">متوسط الإيرادات لكل عميل</p>
              <p className="text-xl font-bold text-gray-900">{analysisData.customerInsights.avgRevenuePerCustomer.toFixed(2)} ر.س</p>
            </div>
          </div>
          <div className="flex items-center gap-4 p-4 bg-gradient-to-br from-blue-50 to-blue-100/50 rounded-xl">
            <div className="p-3 bg-blue-600 text-white rounded-lg">
              <TrendingUp className="w-6 h-6" />
            </div>
            <div>
              <p className="text-sm text-gray-600">معدل النمو</p>
              <p className="text-xl font-bold text-gray-900">{analysisData.performanceMetrics.growthRate.toFixed(1)}%</p>
            </div>
          </div>
        </div>
      </motion.div>

      {/* Print Styles */}
      <style>{`
        @media print {
          .print\\:hidden {
            display: none !important;
          }
        }
      `}</style>
    </div>
  );
};

export default SalesAnalysis;

